import React, { useState, useEffect } from "react";
import { Button, Card, Col, Row, Form, Alert, Spinner, Modal } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency } from "../utils/formatters";
import './Clients.css';
import { API_BASE_URL } from "../constants/api";

export default function ModifierClient() {
    const navigate = useNavigate();
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    
    // Notifications toast
    const [toast, setToast] = useState({ show: false, message: "", type: "success" });
    
    const [clientData, setClientData] = useState({
        code_client: '',
        raison_sociale: '',
        adresse: '',
        telephone: '',
        email: '',
        numero_compte: '',
        ice: '',
        patente: '',
        rc: '',
        identifiant_fiscal: '',
        cnss: '',
        plafond_credit: 0,
        solde: 0,
        date_creation: '',
        date_modification: ''
    });

    // Afficher une notification toast
    const showToast = (message, type = "success") => {
        setToast({ show: true, message, type });
        setTimeout(() => {
            setToast({ show: false, message: "", type: "success" });
        }, 4000);
    };

    useEffect(() => {
        fetchClientDetails();
    }, [id]);

    const fetchClientDetails = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );

            if (!response.ok) {
                if (response.status === 401) {
                    localStorage.removeItem("token");
                    localStorage.removeItem("user");
                    navigate("/pages/signin2");
                    throw new Error("Session expirée");
                }
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const result = await response.json();
            if (result.success) {
                setClientData(result.data);
            } else {
                setError("Client non trouvé");
            }
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setClientData(prev => ({
            ...prev,
            [name]: name === 'plafond_credit' || name === 'solde' ? parseFloat(value) || 0 : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!clientData.raison_sociale) {
            showToast("La raison sociale est obligatoire", "error");
            return;
        }

        setSaving(true);

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?id=${id}`,
                {
                    method: 'PUT',
                    headers: { 
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(clientData)
                }
            );

            const result = await response.json();

            if (response.ok && result.success) {
                showToast("Client mis à jour avec succès !", "success");
                setTimeout(() => {
                    navigate('/ventes/clients/liste');
                }, 2000);
            } else {
                showToast(result.message || "Erreur lors de la mise à jour", "error");
            }
        } catch (err) {
            showToast("Erreur de connexion au serveur", "error");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        setDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/clients.php?id=${id}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );

            const result = await response.json();

            if (result.success) {
                setShowDeleteModal(false);
                showToast("Client supprimé avec succès !", "success");
                setTimeout(() => {
                    navigate('/ventes/clients/liste');
                }, 2000);
            } else {
                showToast(result.message, "error");
                setShowDeleteModal(false);
            }
        } catch (err) {
            showToast("Erreur lors de la suppression", "error");
            setShowDeleteModal(false);
        } finally {
            setDeleting(false);
        }
    };

    const handleCancel = () => {
        navigate('/ventes/clients/liste');
    };

    if (loading) {
        return (
            <React.Fragment>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                        <div className="text-center">
                            <div className="loading-animation mb-4">
                                <div className="loading-ring"><div className="loading-ring-inner"></div></div>
                                <i className="ri-group-line loading-icon"></i>
                            </div>
                            <h5 className="fw-bold text-primary mb-2">Chargement des données</h5>
                            <p className="text-muted small mb-0">Récupération des informations du client...</p>
                        </div>
                    </div>
                    <Footer />
                </div>
                <style>{`
                    .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
                    .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #21ACCD, #1a8ba8); animation: pulse-ring 1.5s ease-in-out infinite; }
                    .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
                    .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #21ACCD; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
                    @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
                    @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
                `}</style>
            </React.Fragment>
        );
    }

    return (
        <React.Fragment>
            <Header />
            <div className="main main-app p-3 p-lg-4">
                
                {/* Toast Notification Fixed */}
                {toast.show && (
                    <div className="toast-notification toast-notification-show">
                        <div className={`toast-content toast-${toast.type}`}>
                            <div className="toast-icon">
                                {toast.type === 'success' ? (
                                    <i className="ri-checkbox-circle-line"></i>
                                ) : (
                                    <i className="ri-error-warning-line"></i>
                                )}
                            </div>
                            <div className="toast-message">
                                <h6>{toast.type === 'success' ? 'Succès' : 'Erreur'}</h6>
                                <p>{toast.message}</p>
                            </div>
                            <button className="toast-close" onClick={() => setToast({ show: false, message: "", type: "success" })}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="toast-progress"></div>
                    </div>
                )}

                {/* En-tête */}
                <div className="d-md-flex align-items-center justify-content-between mb-4">
                    <div>
                        <ol className="breadcrumb fs-sm mb-1">
                            <li className="breadcrumb-item"><Link to="/dashboard">Tableau de bord</Link></li>
                            <li className="breadcrumb-item"><Link to="/ventes">Ventes</Link></li>
                            <li className="breadcrumb-item"><Link to="/ventes/clients/liste">Clients</Link></li>
                            <li className="breadcrumb-item active">Modifier client</li>
                        </ol>
                        <h4 className="main-title mb-0 d-flex align-items-center">
                            <i className="ri-pencil-line me-2" style={{ color: '#21ACCD' }}></i>
                            Modifier le client
                        </h4>
                    </div>
                </div>

                <Row className="justify-content-center">
                    <Col xl="10">
                        <Card className="border-0 shadow-lg rounded-4 overflow-hidden">
                            {/* En-tête de la carte avec code client - Couleur #21ACCD */}
                            <Card.Header className="py-3" style={{ background: '#21ACCD' }}>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <div className="client-avatar-large bg-white rounded-circle d-flex align-items-center justify-content-center me-3"
                                             style={{ width: '60px', height: '60px', fontSize: '24px', color: '#21ACCD' }}>
                                            {clientData.raison_sociale?.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <h5 className="mb-1 text-white">{clientData.raison_sociale}</h5>
                                            <p className="mb-0 text-white-50">
                                                <i className="ri-barcode-line me-1"></i>
                                                Code: {clientData.code_client}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="text-end">
                                        <small className="text-white-50 d-block">Créé le</small>
                                        <span className="text-white">{new Date(clientData.date_creation).toLocaleDateString('fr-FR')}</span>
                                    </div>
                                </div>
                            </Card.Header>

                            <Card.Body className="p-4">
                                <Form onSubmit={handleSubmit}>
                                    <h5 className="mb-4 pb-2 border-bottom">
                                        <i className="ri-information-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Informations générales
                                    </h5>
                                    
                                    <Row className="g-3 mb-4">
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Code client</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="code_client"
                                                    value={clientData.code_client}
                                                    onChange={handleChange}
                                                    disabled
                                                    className="bg-light"
                                                />
                                                <Form.Text className="text-muted">
                                                    Le code client ne peut pas être modifié
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Raison sociale <span className="text-danger">*</span></Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="raison_sociale"
                                                    value={clientData.raison_sociale}
                                                    onChange={handleChange}
                                                    required
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Téléphone</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="telephone"
                                                    value={clientData.telephone}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Email</Form.Label>
                                                <Form.Control
                                                    type="email"
                                                    name="email"
                                                    value={clientData.email}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="12">
                                            <Form.Group>
                                                <Form.Label>Adresse</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={2}
                                                    name="adresse"
                                                    value={clientData.adresse}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <h5 className="mb-4 pb-2 border-bottom">
                                        <i className="ri-briefcase-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Identifiants juridiques
                                    </h5>

                                    <Row className="g-3 mb-4">
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Numéro de compte</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="numero_compte"
                                                    value={clientData.numero_compte}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>ICE</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="ice"
                                                    value={clientData.ice}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Patente</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="patente"
                                                    value={clientData.patente}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>RC</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="rc"
                                                    value={clientData.rc}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Identifiant fiscal</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="identifiant_fiscal"
                                                    value={clientData.identifiant_fiscal}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>CNSS</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="cnss"
                                                    value={clientData.cnss}
                                                    onChange={handleChange}
                                                    className="border-2"
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <h5 className="mb-4 pb-2 border-bottom">
                                        <i className="ri-bank-card-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Paramètres financiers
                                    </h5>

                                    <Row className="g-3 mb-4">
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Plafond de crédit</Form.Label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light">FCFA</span>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="plafond_credit"
                                                        value={clientData.plafond_credit}
                                                        onChange={handleChange}
                                                        className="border-2"
                                                    />
                                                </div>
                                            </Form.Group>
                                        </Col>
                                        <Col md="6">
                                            <Form.Group>
                                                <Form.Label>Solde actuel</Form.Label>
                                                <div className="input-group">
                                                    <span className="input-group-text bg-light">FCFA</span>
                                                    <Form.Control
                                                        type="number"
                                                        step="0.01"
                                                        name="solde"
                                                        value={clientData.solde}
                                                        onChange={handleChange}
                                                        className="border-2"
                                                    />
                                                </div>
                                                <Form.Text className="text-muted">
                                                    {clientData.solde > 0 ? (
                                                        <span className="text-danger">Client débiteur</span>
                                                    ) : clientData.solde < 0 ? (
                                                        <span className="text-success">Client créditeur</span>
                                                    ) : null}
                                                </Form.Text>
                                            </Form.Group>
                                        </Col>
                                    </Row>

                                    <hr className="my-4" />

                                    {/* Boutons d'action */}
                                    <div className="d-flex justify-content-between gap-3">
                                        <div>
                                            <Button 
                                                variant="danger" 
                                                onClick={() => setShowDeleteModal(true)}
                                                disabled={saving}
                                                className="px-4 py-2 rounded-3"
                                                style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', border: 'none' }}
                                            >
                                                <i className="ri-delete-bin-line me-2"></i>
                                                Supprimer le client
                                            </Button>
                                        </div>
                                        <div className="d-flex gap-3">
                                            <Button 
                                                variant="outline-secondary" 
                                                onClick={handleCancel}
                                                disabled={saving}
                                                className="px-5 py-2 rounded-3"
                                            >
                                                Annuler
                                            </Button>
                                            <Button 
                                                variant="primary" 
                                                type="submit"
                                                disabled={saving}
                                                className="px-5 py-2 rounded-3"
                                                style={{ background: '#21ACCD', border: 'none' }}
                                            >
                                                {saving ? (
                                                    <>
                                                        <Spinner size="sm" className="me-2 text-white" />
                                                        Enregistrement...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i className="ri-save-line me-2"></i>
                                                        Enregistrer
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Modal de confirmation de suppression */}
                <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="text-danger">
                            <i className="ri-alert-line me-2"></i>
                            Confirmation de suppression
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-3">
                        <p className="mb-0">
                            Êtes-vous sûr de vouloir supprimer le client <strong>{clientData.raison_sociale}</strong> ?
                        </p>
                        <p className="text-muted small mt-2 mb-0">
                            <i className="ri-information-line me-1"></i>
                            Cette action est irréversible. Toutes les données associées à ce client seront définitivement supprimées.
                        </p>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
                            Annuler
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleDelete}
                            disabled={deleting}
                            style={{ background: 'linear-gradient(135deg, #dc2626, #b91c1c)', border: 'none' }}
                        >
                            {deleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Suppression...
                                </>
                            ) : (
                                <>
                                    <i className="ri-delete-bin-line me-2"></i>
                                    Confirmer la suppression
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            {/* Styles */}
            <style>{`
                .border-2 {
                    border-width: 2px !important;
                    border-color: #e2e8f0;
                }
                .border-2:focus {
                    border-color: #21ACCD;
                    box-shadow: 0 0 0 3px rgba(33, 172, 205, 0.1);
                }
                .input-group-text {
                    border-width: 2px;
                    border-color: #e2e8f0;
                    background: #f8fafc;
                }
                .client-avatar-large {
                    border: 3px solid rgba(255,255,255,0.3);
                    box-shadow: 0 4px 10px rgba(0,0,0,0.2);
                }
                .text-white-50 {
                    color: rgba(255,255,255,0.7);
                }
                
                /* Toast Notifications */
                .toast-notification {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 9999;
                    min-width: 350px;
                    max-width: 450px;
                    background: white;
                    border-radius: 16px;
                    box-shadow: 0 10px 30px rgba(0,0,0,0.15);
                    overflow: hidden;
                    transform: translateX(120%);
                    transition: transform 0.3s ease-out;
                    opacity: 0;
                }
                
                .toast-notification-show {
                    transform: translateX(0);
                    opacity: 1;
                }
                
                .toast-content {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    padding: 16px 20px;
                    position: relative;
                }
                
                .toast-success {
                    border-left: 4px solid #10b981;
                }
                
                .toast-error {
                    border-left: 4px solid #ef4444;
                }
                
                .toast-icon {
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 20px;
                }
                
                .toast-success .toast-icon {
                    background: #ecfdf5;
                    color: #10b981;
                }
                
                .toast-error .toast-icon {
                    background: #fef2f2;
                    color: #ef4444;
                }
                
                .toast-message {
                    flex: 1;
                }
                
                .toast-message h6 {
                    margin: 0 0 4px 0;
                    font-size: 0.9rem;
                    font-weight: 600;
                }
                
                .toast-message p {
                    margin: 0;
                    font-size: 0.8rem;
                    color: #64748b;
                }
                
                .toast-close {
                    background: transparent;
                    border: none;
                    color: #94a3b8;
                    cursor: pointer;
                    padding: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    transition: color 0.2s;
                }
                
                .toast-close:hover {
                    color: #1e293b;
                }
                
                .toast-progress {
                    position: absolute;
                    bottom: 0;
                    left: 0;
                    height: 3px;
                    width: 100%;
                    background: rgba(0,0,0,0.05);
                    animation: progress 4s linear forwards;
                }
                
                .toast-success .toast-progress {
                    background: linear-gradient(90deg, #10b981, #059669);
                }
                
                .toast-error .toast-progress {
                    background: linear-gradient(90deg, #ef4444, #dc2626);
                }
                
                @keyframes progress {
                    0% { width: 100%; }
                    100% { width: 0%; }
                }
                
                @media (max-width: 768px) {
                    .toast-notification {
                        top: 10px;
                        right: 10px;
                        left: 10px;
                        min-width: auto;
                        max-width: none;
                    }
                }
            `}</style>
        </React.Fragment>
    );
}